import { ApplicationRef, Injectable, inject } from '@angular/core';
import { SwUpdate, type VersionReadyEvent } from '@angular/service-worker';
import {
  catchError,
  EMPTY,
  filter,
  first,
  from,
  interval,
  type Observable,
  Subject,
  switchMap,
  throttleTime,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ApplicationUpdateService {
  private swUpdate = inject(SwUpdate);
  private appRef = inject(ApplicationRef);

  private windowFocused$ = new Subject<void>();

  init() {
    // When a new version is detected the page will be reloaded
    // The SwUpdate service will check for an update on page load and when checkForUpdate() is called (in the functions below)
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        window.location.reload();
      });

    this.swUpdate.unrecoverable.subscribe((evt) => {
      console.error(`An error occurred that we cannot recover from:\n${evt.reason}`);
    });

    // Only check for an update once every 5 minutes on the first window refocus
    this.windowFocused$.pipe(throttleTime(5 * 60 * 1000), this.checkForUpdateOperator()).subscribe();

    // On window focus check for an update
    window.onfocus = () => this.windowFocused$.next();

    // Once the app is stable check for an update every 5 minutes
    this.appRef.isStable
      .pipe(first((isStable) => isStable === true))
      .pipe(
        switchMap(() => interval(5 * 60 * 1000)),
        this.checkForUpdateOperator()
      )
      .subscribe();
  }

  private checkForUpdateOperator() {
    return (source$: Observable<unknown>) =>
      source$.pipe(
        switchMap(() => from(this.swUpdate.checkForUpdate())),
        catchError((err) => {
          console.error('Failed to check for updates:', err);
          return EMPTY;
        })
      );
  }
}
